import React, { useEffect } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { SURFACE } from "../../dark-theme/palette";
import { trackEvent } from "../../services/GoogleAnalytics";
import { usePagesTranslation } from "../locales";
import LegalAppLink from "../components/LegalAppLink";
import LegalCompanyIdentity from "./LegalCompanyIdentity";

export default function PrivacyPolicyMain() {
    const { t } = usePagesTranslation();
    const lastUpdated = process.env.REACT_APP_PRIVACY_POLICY_LAST_UPDATED || new Date().toLocaleDateString();

    useEffect(() => {
        trackEvent('entered', { name: 'PrivacyPolicyMain' });
    }, []);

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                px: { xs: 1.5, sm: 3, md: 6 },
                pt: { xs: 1, sm: 3, md: 6 },
                pb: { xs: 2, sm: 4, md: 6 },
            }}
        >
            <Grid container spacing={2}>
                <Grid size={{ xs: 12 }}>
                    <Paper
                        sx={{
                            p: 1,
                            width: "100%",
                            boxShadow: "none",
                            backgroundColor: SURFACE.card,
                            color: (theme) => theme.palette.text.secondary,
                            textAlign: "left",
                        }}
                    >
                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3, textAlign: 'left' }}>
                            {t("core.privacy.title")}
                        </Typography>
                        
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.lastUpdated", { date: lastUpdated })}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s1Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s1Body")}
                        </Typography>

                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s1LangNote")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s1NoDeviceLocation")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s2Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s2Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s3Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s3Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s3aTitle")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s3aBody")}
                        </Typography>
                        <Box component="ul" sx={{ pl: 3, m: 0, textAlign: "left", "& li": { mb: 1, textAlign: "left" }, "& li span": { display: "block", textAlign: "left" } }}>
                            <li><Typography component="span" variant="body1">{t("core.privacy.s3aGA")}</Typography></li>
                            <li><Typography component="span" variant="body1">{t("core.privacy.s3aAdSense", "Google AdSense (third-party advertising): Serves ads on our website using cookies and similar technologies. Google and its advertising partners may collect data such as cookie identifiers, approximate location derived from IP address, and browsing activity on this site to show relevant ads and measure ad performance. We do not sell your personal information to advertisers.")}</Typography></li>
                            <li><Typography component="span" variant="body1">{t("core.privacy.s3aStripe")}</Typography></li>
                            <li><Typography component="span" variant="body1">{t("core.privacy.s3aAWS")}</Typography></li>
                        </Box>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s4Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s4Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s5Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s5Body")}
                        </Typography>
                        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mt: 2, mb: 1.5, textAlign: "left" }}>
                            {t("core.privacy.s5AdSenseTitle", "Google AdSense, cookies, and third-party advertising vendors")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "left" }}>
                            {t(
                                "core.privacy.s5AdSenseBody",
                                "This site uses Google AdSense to display advertising. AdSense uses cookies and similar tracking technologies to serve ads based on your prior visits to this website and other sites, to limit how often you see an ad, and to measure ad performance. Third-party vendors, including Google, use cookies to serve ads. Google's use of advertising cookies enables it and its partners to serve ads to you based on your visit to this site and/or other sites on the Internet.",
                            )}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "left" }}>
                            {t(
                                "core.privacy.s5AdVendors",
                                "Third-party advertising vendors (including Google and certified AdSense partners) may collect or receive information from our website and use cookies, web beacons, and similar technologies. These vendors may combine that information with information collected from other websites. We do not control those vendors' cookies. You can opt out of personalized advertising by visiting Google Ads Settings, and you can learn how Google uses data from partner sites on Google's Partner Sites policy page. You may also opt out of some third-party vendor cookies through the Network Advertising Initiative opt-out page.",
                            )}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "left" }}>
                            <Link href="https://www.google.com/settings/ads" target="_blank" rel="noopener noreferrer">
                                Google Ads Settings
                            </Link>
                            {" · "}
                            <Link href="https://policies.google.com/technologies/partner-sites" target="_blank" rel="noopener noreferrer">
                                How Google uses information from sites that use our services
                            </Link>
                            {" · "}
                            <Link href="https://optout.networkadvertising.org/" target="_blank" rel="noopener noreferrer">
                                Network Advertising Initiative opt-out
                            </Link>
                            {" · "}
                            <Link href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer">
                                Google advertising cookies
                            </Link>
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: "left" }}>
                            {t(
                                "core.privacy.s5AdOptOut",
                                "You can instruct your browser to refuse all cookies or to indicate when a cookie is being sent. If you disable cookies, some features of the site (including signed-in play and ad frequency capping) may not work correctly. Using a cookie opt-out does not mean you will no longer see ads; it means the ads you see may be less relevant.",
                            )}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s6Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s6Body")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s6Body2")}{" "}
                            <LegalAppLink to="/deletion-status" sx={{ fontWeight: 600 }}>
                                {t("core.privacy.s6DataDeletionLink")}
                            </LegalAppLink>.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s6Body3")}{" "}
                            <LegalAppLink to="/data-deletion/request" sx={{ fontWeight: 600 }}>
                                {t("core.privacy.s6DataDeletionRequestLink")}
                            </LegalAppLink>.
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s6RetentionBody")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s7Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s7Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s8Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s8Body")}
                        </Typography>

                        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 4, mb: 2, textAlign: 'left' }}>
                            {t("core.privacy.s9Title")}
                        </Typography>
                        <Typography variant="body1" paragraph sx={{ textAlign: 'left' }}>
                            {t("core.privacy.s9Body")}
                        </Typography>
                        <LegalCompanyIdentity />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}
